import React, {useCallback, useEffect, useLayoutEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Box} from "@mui/material";
import {matchPath, useHistory, useLocation} from "react-router-dom";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import RegionRatesSection from "./sections/region-rates";
import ProductFlatRatesSection from "./sections/product-flat-rates";


const ShippingRatesTabs = {
    regionRates: 'regionRates',
    productFlatRates: 'productFlatRates',
};

/**
 * Shipping rates shell: access-aware title/tabs and URL-synced region / product flat rate panels.
 */
const ShippingRatesView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.shipping-fees.shipping-rates'});
    const operatorAccessLevels = useAccessLevels();
    const history = useHistory();
    const location = useLocation();
    const {configureNotebookTags} = useConfigureNotebook('shipping-rates-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const canRegion = operatorAccessLevels.packages.sales.regionRates;
    const canPart = operatorAccessLevels.packages.sales.partRates;
    const showTabs = canRegion && canPart;

    /**
     * Configures notebook and behavioral analytics tags on mount.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.shippingFeesShippingRates);
        configureBehavioralTool((tags) => tags.shippingFeesShippingRates);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Resolves the preferred child path for the current user's access.
     *
     * @returns {string | null}
     */
    const getPreferredChildPath = useCallback(() => {
        if (canRegion)
            return routes.shippingFees.shippingRates.regionRates;
        if (canPart)
            return routes.shippingFees.shippingRates.productFlatRates;
        return null;
    }, [canPart, canRegion]);

    /**
     * Redirects bare base URL and unauthorized child suffixes to an allowed path.
     */
    useLayoutEffect(() => {
        const errorPath = routes.shippingFees.base.concat(routes.error.serverErrorForSection);
        const preferred = getPreferredChildPath();

        if (matchPath(location.pathname, {path: routes.shippingFees.shippingRates.base, exact: true})) {
            if (preferred)
                return history.replace(preferred);
            return history.replace(errorPath);
        }

        if (matchPath(location.pathname, {path: routes.shippingFees.shippingRates.regionRates, exact: true})) {
            if (!canRegion) {
                if (canPart)
                    return history.replace(routes.shippingFees.shippingRates.productFlatRates);
                return history.replace(errorPath);
            }
            return;
        }

        if (matchPath(location.pathname, {path: routes.shippingFees.shippingRates.productFlatRates, exact: true})) {
            if (!canPart) {
                if (canRegion)
                    return history.replace(routes.shippingFees.shippingRates.regionRates);
                return history.replace(errorPath);
            }
        }
    }, [canPart, canRegion, getPreferredChildPath, history, location.pathname]);

    /**
     * Active tab derived from the current pathname.
     */
    const activeTab = useMemo(() => {
        if (matchPath(location.pathname, {path: routes.shippingFees.shippingRates.productFlatRates, exact: true}))
            return ShippingRatesTabs.productFlatRates;
        return ShippingRatesTabs.regionRates;
    }, [location.pathname]);

    /**
     * Navigates to the selected tab's route.
     *
     * @param {*} _event
     * @param {string} newTab
     */
    const onTabChanged = useCallback((_event, newTab) => {
        if (newTab === ShippingRatesTabs.productFlatRates)
            history.push(routes.shippingFees.shippingRates.productFlatRates);
        else
            history.push(routes.shippingFees.shippingRates.regionRates);
    }, [history]);

    /**
     * Page title based on whether both sections are available.
     */
    const pageTitle = useMemo(() => {
        if (showTabs)
            return t('title');
        if (canPart && !canRegion)
            return t('tabs.productFlatRates');
        return t('tabs.regionRates');
    }, [canPart, canRegion, showTabs, t]);

    const showRegionPanel = canRegion && (
        !showTabs || activeTab === ShippingRatesTabs.regionRates
    );
    const showProductPanel = canPart && (
        !showTabs || activeTab === ShippingRatesTabs.productFlatRates
    );

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 4}}>
            <h2 className={'text-xl font-weight-semi-bold m-0'}>
                {pageTitle}
            </h2>
            {
                showTabs &&
                <Tabs value={activeTab} onChange={onTabChanged}>
                    <Tab
                        label={t('tabs.regionRates')}
                        value={ShippingRatesTabs.regionRates}
                    />
                    <Tab
                        label={t('tabs.productFlatRates')}
                        value={ShippingRatesTabs.productFlatRates}
                    />
                </Tabs>
            }
            {showRegionPanel && <RegionRatesSection/>}
            {showProductPanel && <ProductFlatRatesSection/>}
        </Box>
    );
};

export default ShippingRatesView;
